/**
 * Theme management — dark (default) vs light.
 *
 * The design system defines tokens for both in [data-theme="dark"] and
 * [data-theme="light"] selectors. We just toggle the attribute on <html>
 * and persist the choice to localStorage.
 *
 * The initial theme is applied BEFORE React renders (in main.tsx) to
 * avoid a "flash of wrong theme" on page load.
 */

export type Theme = "dark" | "light";

const STORAGE_KEY = "bht_theme";

export function getInitialTheme(): Theme {
  // 1. Honor explicit user choice if set
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "dark" || stored === "light") return stored;
  } catch {
    // localStorage blocked (private mode, etc.) — fall through
  }
  // 2. Fall back to system preference
  if (typeof window !== "undefined" && window.matchMedia) {
    if (window.matchMedia("(prefers-color-scheme: light)").matches) {
      return "light";
    }
  }
  // 3. Default to dark (matches the brand identity)
  return "dark";
}

export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

export function setTheme(theme: Theme): void {
  applyTheme(theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Ignore — theme still applied for this session
  }
  // Notify any listening components so they can re-render
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("bht:theme-changed", { detail: theme }));
  }
}

export function getCurrentTheme(): Theme {
  if (typeof document === "undefined") return "dark";
  const attr = document.documentElement.getAttribute("data-theme");
  return attr === "light" ? "light" : "dark";
}

export function toggleTheme(): Theme {
  const next: Theme = getCurrentTheme() === "dark" ? "light" : "dark";
  setTheme(next);
  return next;
}
